import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from '../ui/badge';
import { GripVertical, Scissors, Trash2, RotateCcw, FileText } from 'lucide-react';

const STATUS_COLORS = {
  scheduled: 'bg-blue-50 border-blue-200 text-blue-700',
  in_progress: 'bg-amber-50 border-amber-200 text-amber-700',
  completed: 'bg-green-50 border-green-200 text-green-700',
  rolled_over: 'bg-gray-50 border-gray-200 text-gray-500',
};

const STATUS_LABELS = {
  scheduled: '待开始',
  in_progress: '进行中',
  completed: '已完成',
  rolled_over: '已滚动',
};

// Next-day rolled task: visually distinct
const ROLLED_NEXT_DAY_CLASS = 'bg-purple-50 border-purple-200 text-purple-700';

function ScheduleTaskCard({ schedule, isOverlay = false, onDelete, onSplit, onExportProcess }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `board-task-${schedule._id}`,
    data: {
      type: 'board-task',
      scheduleId: schedule._id,
      schedule,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isRolledNextDay = !!schedule.rolled_from_schedule_id;
  const isCompleted = schedule.status === 'completed';

  const statusClass = isRolledNextDay
    ? ROLLED_NEXT_DAY_CLASS
    : (STATUS_COLORS[schedule.status] || STATUS_COLORS.scheduled);

  const progress = schedule.assigned_quantity > 0
    ? Math.round((schedule.completed_quantity / schedule.assigned_quantity) * 100)
    : 0;

  const tooltipText = `${schedule.step_name}\n${schedule.production_order_number} · ${schedule.finished_product_code}\n${schedule.finished_product_name || ''}\n进度: ${schedule.completed_quantity}/${schedule.assigned_quantity} 件 (${progress}%)\n状态: ${isRolledNextDay ? '滚动' : (STATUS_LABELS[schedule.status] || schedule.status)}`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      title={tooltipText}
      className={`
        p-2 mb-1.5 border rounded-md cursor-grab active:cursor-grabbing text-xs
        ${statusClass}
        ${isDragging || isOverlay ? 'shadow-lg ring-2 ring-blue-400 opacity-90 z-50' : ''}
      `}
    >
      <div className="flex items-center gap-1 mb-1">
        <GripVertical className="h-3 w-3 opacity-50 shrink-0" />
        <span className="font-semibold truncate flex-1" title={schedule.step_name}>
          {schedule.step_name}
        </span>
        {isRolledNextDay ? (
          <Badge variant="outline" className="text-[9px] h-4 px-1 shrink-0 bg-purple-100 text-purple-700 border-purple-300">
            <RotateCcw className="h-2.5 w-2.5 mr-0.5" />
            滚动
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[9px] h-4 px-1 shrink-0">
            {STATUS_LABELS[schedule.status] || schedule.status}
          </Badge>
        )}
      </div>

      <div className="text-[10px] text-blue-600 font-semibold truncate mb-0.5" title={`${schedule.production_order_number}-${String(schedule.item_index + 1).padStart(3, '0')}`}>
        {schedule.production_order_number}-{String(schedule.item_index + 1).padStart(3, '0')}
      </div>

      <div className="text-[10px] opacity-80 truncate mb-0.5" title={`${schedule.finished_product_code}${schedule.specification ? ' · ' + schedule.specification : ''}`}>
        {schedule.finished_product_code}
        {schedule.specification && <span className="text-amber-600 font-medium ml-1">({schedule.specification})</span>}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-[10px]">
          {schedule.completed_quantity}/{schedule.assigned_quantity} 件
        </span>
        {!isOverlay && (
          <div className="flex items-center gap-0.5">
            <button
              onClick={(e) => { e.stopPropagation(); onExportProcess?.(schedule); }}
              className="p-0.5 hover:bg-black/5 rounded"
              title="导出工序文件"
            >
              <FileText className="h-3 w-3" />
            </button>
            {!isCompleted && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); onSplit?.(schedule); }}
                  className="p-0.5 hover:bg-black/5 rounded"
                  title="拆分"
                >
                  <Scissors className="h-3 w-3" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete?.(schedule._id); }}
                  className="p-0.5 hover:bg-black/5 rounded"
                  title="删除"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {progress > 0 && progress < 100 && (
        <div className="mt-1 h-1 bg-black/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-current opacity-60 rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}

export default ScheduleTaskCard;
